import { useEffect, FC, useMemo, useRef } from "react"
import { useQueryClient } from "react-query"

import { Formik, Form, FormikValues, FieldArray, FormikProps} from 'formik'
import * as Yup from 'yup'

import { KTSVG } from "../../../../../../../_metronic/helpers"

import { DLMApplication } from "../../../_models"
import { submitNewDLMApplication, submitDLMPhase_1_2, editDLMApplication } from "../../../_requests"
import { doFormSubmit, isPopulatedArray, processNoteValues } from "../../../../../../components/helpers"
import {
  StatusErrorMessage,
  FormikTextInput, FormikTextField, FormikFileUpload,
  FileUploaderResponse,
  FormikAcknowledgeFeeCheck,
  FormikNotesArea
} from "../../../../../../components/widgets"

import { AttachmentCard } from "../../../../attachments/AttachmentCard"

import { cloneDeep, orderBy, pickBy } from "lodash"
import { SubmitButtonRow } from "../../../../../../components/layout"
import { DOCHourRatingDropdown } from "../../form"
import { SCDFPlanNoSchema } from "../../.."
import { IWebFileAttachment } from "../../../../applications"


interface IDOCSplitSiteModel {
  id?: number,
  projectName: string
  projectLocation: string
  installerName: string
  bpNumber: string
  originalCoverageArea: number
  coverageArea: number
  unit:  string
  hourRating: string
  otherReference: string
}

interface IDOCSplitFormModel {
  sites: Array<IDOCSplitSiteModel>    
  unit: string
  coverageArea: number
  otherAttachments: Array<IWebFileAttachment>
  comment: string
  note: string
  noteAttachments: Array<IWebFileAttachment>
  acknowledgeFee: boolean
}

const docSplitSiteSchema: any = Yup.object({
  id: Yup.number().integer(),
  projectName: Yup.string().trim().label('Project Name').required(),
  projectLocation: Yup.string().trim().label('Project Location').required(),
  installerName: Yup.string().trim().label('Installer Company').required(),
  bpNumber: SCDFPlanNoSchema,
  originalCoverageArea: Yup.number(),
  coverageArea: Yup.number().label('Area of Coverage').required()
  .test({
    name: 'less-than-original',
    test: function (value) {
      const currentValue = parseFloat(`0${value}`);
      
      if (currentValue <= 0) {
        return this.createError({
          message: "Must be greater than zero!"
        });
      }

      const originalValue = parseFloat(`0${this.resolve(Yup.ref('originalCoverageArea'))}`);

      if (currentValue > originalValue) {
        return this.createError({
          message: `Must be less than ${originalValue}!`
        });
      }

      return true;
    }
  }),
  unit: Yup.string().trim(),
  hourRating: Yup.string().trim().matches(/^(?!0$)(?!0\.0$)(-|\d+(\.\d{1})?)$/, "Select from options.").required().label('Hour Rating'),
  otherReference: Yup.string().trim().label('Other Reference'),
});


const docSplitSchema: any = Yup.object({
  sites: Yup.array().of(docSplitSiteSchema).min(2, "Need at least 2 sites.").required().label('Sites'),
  otherAttachments: Yup.array(),
  comment: Yup.string().trim().label('Reason').required(),
  note: Yup.string().trim().label("Notes"),
  noteAttachments: Yup.array(),  
  acknowledgeFee: Yup.bool().oneOf([true], 'Must be checked').label('Acknowledge Fee')
});

const initDOCSiteData: IDOCSplitSiteModel = {
  projectName: '',
  projectLocation: '',
  installerName: '',
  bpNumber:  '',
  originalCoverageArea: 0,
  coverageArea: 0,
  unit:  '',
  hourRating: '',
  otherReference: '',
}


const initDOCSplitFormValues: IDOCSplitFormModel = {
  sites:[
    { ...initDOCSiteData},
    { ...initDOCSiteData},
  ],
  unit: '',
  coverageArea: 0,
  otherAttachments: [],
  comment: '',
  note: '',
  noteAttachments: [],  
  acknowledgeFee: false
}

const DOCSPLIT_SITEFIELDS = [
  'id',
  'projectName',
  'projectLocation',
  'installerName',
  'bpNumber',  
  'coverageArea',
  'unit',
  'hourRating',
  'otherReference'
];

type FormProps = {
  initSite: IDOCSplitSiteModel
  doClose: Function
  resubmitMode?: string
}

const DLMDOCSplitForm: FC<FormikProps<IDOCSplitFormModel> & FormProps> = ({status, isSubmitting, values, setFieldValue, doClose, initSite, resubmitMode}) => {

  const timerRef = useRef<any>();

  const rebalanceSites = (idx: number, newValue: number) => {
    let _origVal = initSite.originalCoverageArea;
    let _val = (newValue > 0) ? newValue: 0;
    if (newValue > _origVal) {
      _val = _origVal;      
    }
    if (_val !== newValue) {
      setFieldValue(`sites.${idx}.coverageArea`, _val, true);
    }

    let _remain = _origVal - _val;
    values.sites.forEach((_site, _sidx) => {
      if (_sidx === 0 || _sidx === idx) return;
      let _aoc = parseFloat(`0${_site.coverageArea}`)
      let _min = Math.min(_remain, _aoc);
      if (_min !== _aoc) {
        setFieldValue(`sites.${_sidx}.coverageArea`, _min, true);
      }
      if (_remain > 0) {
        _remain -= _min;
      }
    })

    // push remaining to first site
    let _firstaoc = parseFloat(`0${values.sites[0].coverageArea}`)
    if (_remain !== _firstaoc) {
      setFieldValue(`sites.0.coverageArea`, _remain, true);
    }    
  }

  useEffect(() => {
    return(() => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined
      }
    })
  },[])

  return(
  <Form id='kt_docsplit_form' className='form' noValidate>  
    <FieldArray
      name="sites"
      render={arrayHelpers => (
        <>
        <div className="d-flex flex-row justify-content-between align-items-center gap-2">
          <div>
            <h4 className="text-center">{`Total Coverage Area :  ${initSite.originalCoverageArea} ${initSite.unit}`}</h4>
          </div>
          <div className="d-flex flex-row gap-3">
            <div className="fs-5">
              Split into <span className="fw-bolder">{`${values.sites.length}`}</span> sites.
            </div>
            <button
              type="button"
              className="siteBtn btn btn-light-success text-success btn-active-success px-2 py-1 lh-0"
              onClick={() => { arrayHelpers.push({ ...initSite, }); }}
              title="Click to Add Site"            
            >
              <KTSVG path='/media/icons/duotune/arrows/arr017.svg' className='svg-icon m-0' />
            </button>          
          </div>
        </div>
        <div className="d-flex flex-row align-start gap-2 overflow-x-auto my-1">
        { [...Array(values.sites.length)].map((_ , idx) => (
          <div key={`split_site_${idx}`} className="p-4 border rounded min-w-350px mw-400px my-2">
            <div className="d-flex flex-row justify-content-between align-items-center gap-2">
              <h4 className="text-center">{`Site ${idx+1}`}</h4>
              {(idx > 1) && 
              <button
                type="button"
                className="siteBtn btn btn-light-danger text-danger btn-active-danger px-2 py-1 lh-0"
                onClick={() => {
                  arrayHelpers.remove(idx);                                  
                }}
                title={`Click to delete site № ${idx + 1}`}
                  
              >
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon m-0' />
              </button>
              }                              
            </div>
            <div className='separator mb-2 opacity-75' />
            <div className="row mb-1">
              <FormikTextField size="12" label="Project Name" name={`sites.${idx}.projectName`} required rows={1} />
            </div>
            <div className="row mb-1">
              <FormikTextField size="12" label="Project Location" name={`sites.${idx}.projectLocation`} required rows={2} />
            </div>
            <div className="row mb-1">
              <FormikTextInput size="12" label="Installer Company" name={`sites.${idx}.installerName`} required />
            </div>
            <div className="row mb-1">
              <FormikTextInput size="12" label="SCDF Building Plan No" name={`sites.${idx}.bpNumber`} />
            </div>
            <div className="row mb-1">
              <FormikTextInput size="5" label="Coverage Area" name={`sites.${idx}.coverageArea`} required readOnly={idx === 0}
                onChange={(e: any) => {
                  //rebalance sites
                  if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = undefined
                  }            
                  timerRef.current = setTimeout(() => {
                    rebalanceSites(idx, parseFloat(`0${e.target.value}`));                    
                  }, 800);                    
                }} 
              />
              <FormikTextInput size="3" label="Unit" name={`sites.${idx}.unit`} readOnly />
              <DOCHourRatingDropdown size="4" label="Hour Rating" name={`sites.${idx}.hourRating`} required={true} />
            </div>
            <div className="row mb-1">
              <FormikTextInput size="12" label="Other Reference Number (DSTA Number)" name={`sites.${idx}.otherReference`} />
            </div>
          </div>
        ))}
        </div>
        </>                    
      )}
    />
    
    <div className="mb-2 row">
      <FormikFileUpload size="6" label="Other Attachments"  name="otherAttachments" acceptFiles="ApplicantFileFormats" isFileArray={true} />
    </div>
    <FieldArray
      name="otherAttachments"
      render={arrayHelpers => (
      <div className="d-flex flex-row flex-wrap gap-2 mh-80px overflow-auto">
      { (values.otherAttachments && values.otherAttachments.length > 0) &&
      values.otherAttachments.map( (o: FileUploaderResponse, idx: number) => (
        <AttachmentCard
          key={`otherAttachment${idx}`}
          className="border py-1"
          idx={idx}
          fileId={o.id}
          fileName={o.name}
          onRemove={(e: any) => {
            e.stopPropagation();
            const elem = e.target.closest('.attachment-card');
            if (elem) {
              const {idx} = elem.dataset;
              arrayHelpers.remove(idx);
            }
          }}
        />
      ))
      }
      </div>
      )}
    />

    <div className="row mb-2 mt-4">
      <FormikTextField size="12" label="Reason" name="comment" required={true} rows={2} />
    </div>
    { resubmitMode &&
    <>
    <div className='separator border-gray-200 my-4'></div>
    <FormikNotesArea attachLabel="Supporting Document/s" />                
    </>
    }

    <FormikAcknowledgeFeeCheck dlmtype="DOC" />

    <StatusErrorMessage status={status} />
    <SubmitButtonRow doClose={() => doClose()} />
  </Form>
  )

}


type Props = {
  docId: number
  docNo: string
  appData: DLMApplication
  doClose: Function
  onSuccess: Function
  appId?: number
  resubmitMode? : string
}

const DLMDOCSplitModal:FC<Props> = ({docId, docNo, appData, doClose, onSuccess, appId, resubmitMode = ''}) => {

  const queryClient = useQueryClient();
  
  const { initValues, initSite } = useMemo(() => {

    let _init = cloneDeep(initDOCSplitFormValues);        
    let _initSite: IDOCSplitSiteModel = {...initDOCSiteData};
  
    if (! appData) return {initValues: _init, initSite: _initSite};

    let _coverageArea = appData?.coverageArea ? appData.coverageArea : 0;

    let _sites: Array<IDOCSplitSiteModel> = [
      {...initDOCSiteData},
      {...initDOCSiteData}
    ];
      
    if (appData?.siteApplications && isPopulatedArray(appData.siteApplications)) {
      _sites = orderBy(appData.siteApplications, ['id'],['asc']).map( (site) => ({
        ...initDOCSiteData,
        ...pickBy(site, (value, key) => (value && DOCSPLIT_SITEFIELDS.includes(key))),
        hourRating: (site?.hourRating && [1,1.5,2].includes(parseFloat(`0${site.hourRating}`))) ? site.hourRating : '-',
        originalCoverageArea: _coverageArea
      }));
    }

    if (_sites && isPopulatedArray(_sites)) {
      Object.assign(_init, { sites: _sites} );
      _initSite = {
        ..._sites[0],
        coverageArea: 0,
      }
    }
    
    if (appData?.otherAttachments && isPopulatedArray(appData.otherAttachments) ) {
      Object.assign(_init, { otherAttachments: appData.otherAttachments.map( file => ({id: file.id, name: file.fileName}))});
    }

    return {initValues: _init, initSite: _initSite};

  }, [appData]);


  const submitFunc = useMemo(() => {
    switch(resubmitMode) {
      case 'resubmit':
        return submitDLMPhase_1_2;
      case 'edit':
        return editDLMApplication;
      default:
        return submitNewDLMApplication;
    }
  }, [resubmitMode]);

  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;
    const { comment, otherAttachments, sites } = values;

    let refSite = isPopulatedArray(sites) ? sites[0] : { ...initDOCSiteData};
    
    let _payload = {
      targetDOC: docId,
      applicationType: "TYPE_DOCSPLIT",
      dlmWebPhase0SitesModel : values.sites.map((site:any) => ({
        ...pickBy(site, (value, key) => (value && DOCSPLIT_SITEFIELDS.includes(key))),
        coverageArea: parseFloat(`0${site.coverageArea}`)
      })),        
      docSplitArea: refSite.coverageArea,
      coverageArea: refSite.originalCoverageArea,
      unit: refSite.unit,
    };

    if (comment) {
      Object.assign(_payload, { comment });
    }
    
    if (isPopulatedArray(otherAttachments)) {
      Object.assign(_payload, {otherAttachments: otherAttachments.map((o: any) => (o.id))});
    }

    if (appId) {
      Object.assign(_payload, { id: appId});
    }

    if (resubmitMode) {
      //@ts-ignore
      _payload = { application: _payload }
      processNoteValues(values, _payload);      
    }

    await doFormSubmit(submitFunc, _payload, setStatus, setSubmitting, (res) => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'doc-applications-list' } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      if (appId) {
        queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appId } ]);      
      }
      if (res?.data) {
        onSuccess(res.data);
      }      
    });
  }

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

    return (
    <>
    <div
      className='modal fade show d-block'
      id='kt_modal_doc_split'
      role='dialog'
      tabIndex={-1}
      aria-modal='true'
    >
      <div className='modal-dialog modal-dialog-centered mw-900px'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h2 className='fw-bolder'>Split DOC : {`${docNo}`}</h2>
            <div
              className='btn btn-icon btn-sm btn-active-icon-primary'
              data-kt-applicants-modal-action='close'
              onClick={()=> { doClose()}}
              style={{cursor: 'pointer'}}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </div>
          </div>
          <div className='modal-body scroll-y mx-1 mb-7'>
            <Formik
              initialValues={initValues}
              validationSchema={docSplitSchema}
              enableReinitialize={true}
              validateOnMount={true}
              initialTouched={{
                projectName: true,
                projectLocation: true,
                installerName: true,
                hourRating: true,
                comment: true,
                unit: true,
              }}
              onSubmit={doSubmit}
              component={(props: any) => <DLMDOCSplitForm doClose={doClose} initSite={initSite} resubmitMode={resubmitMode} {...props} />  }            
            />            
        </div>
      </div>
    </div>
  </div>
  <div className='modal-backdrop fade show'></div>
  </>
  )
}

export {DLMDOCSplitModal}